import { useState, useEffect, useRef } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { 
  Box, IconButton, Typography, useMediaQuery, Avatar, Divider, 
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemIcon, Paper, Slider,
  Tooltip, CircularProgress, Snackbar, Alert
} from "@mui/material";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import apiClient from "@/api/client";
import { getPreferences } from "@/api/preferencesService";

// Icons
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ReorderIcon from '@mui/icons-material/Reorder';
import WidthNormalIcon from '@mui/icons-material/WidthNormal';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

// Coordinator Icons
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import RiceBowlIcon from '@mui/icons-material/RiceBowl';
import LunchDiningOutlinedIcon from '@mui/icons-material/LunchDiningOutlined';
import FreeBreakfastOutlinedIcon from '@mui/icons-material/FreeBreakfastOutlined';
import SelfImprovementOutlinedIcon from '@mui/icons-material/SelfImprovementOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import TempleBuddhistOutlinedIcon from '@mui/icons-material/TempleBuddhistOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import LocalCafeOutlinedIcon from '@mui/icons-material/LocalCafeOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

// --- Item Component (fixed: no nested anchor) ---
const Item = ({ title, to, icon, selected, setSelected, collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname.split('/').filter(Boolean).includes(to) ||
    (to === "dashboard" && location.pathname === "/admin");

  const handleClick = () => {
    setSelected(title);
    navigate(`/admin/${to.replace(/^\/+/, "")}`);
  };

  return (
    <MenuItem
      active={isActive}
      style={{
        color: isActive ? "#1976d2" : "#4b5563",
        backgroundColor: isActive ? "#e3f2fd" : "transparent",
        borderRadius: "8px",
        margin: "4px 8px",
        transition: "all 0.2s ease-in-out",
      }}
      onClick={handleClick}
      icon={icon}
    >
      <Typography
        sx={{
          fontSize: "0.9rem",
          fontWeight: isActive ? 600 : 400,
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </Typography>
    </MenuItem>
  );
};

// --- Main Sidebar Component ---
const SidebarComponent = () => {
  const [selected, setSelected] = useState("Dashboard");
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [isCollapsed, setIsCollapsed] = useState(isMobile);
  const [openSubMenus, setOpenSubMenus] = useState({});
  const [userRoles, setUserRoles] = useState([]);
  const [menuOrder, setMenuOrder] = useState([]);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [tempMenuOrder, setTempMenuOrder] = useState([]);
  const [sidebarWidth, setSidebarWidth] = useState(250);
  const [isResizing, setIsResizing] = useState(false);
  const [widthDialogOpen, setWidthDialogOpen] = useState(false);
  const [tempWidth, setTempWidth] = useState(250);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const sidebarRef = useRef(null);

  
  // Load user data and preferences from server
  useEffect(() => {
    let active = true;

    const loadUserData = async () => {
      // Load roles from localStorage (set during login)
      const rolesStr = localStorage.getItem("roles");

      if (rolesStr) {
        try {
          const roles = JSON.parse(rolesStr);

          if (active) {
            setUserRoles(roles);
          }
        } catch (e) {
          console.error("Error parsing roles:", e);
        }
      }

      try {
        const preferences = await getPreferences();

        if (!active) return;

        // Load menu order
        if (
          preferences.sidebar_menu_order &&
          Array.isArray(preferences.sidebar_menu_order)
        ) {
          setMenuOrder(preferences.sidebar_menu_order);
        }

        // Load sidebar width
        if (preferences.sidebar_width) {
          const width = parseInt(preferences.sidebar_width, 10);
          setSidebarWidth(width);
          setTempWidth(width);
        }

        // Load collapsed state
        if (
          preferences.sidebar_collapsed !== undefined &&
          preferences.sidebar_collapsed !== null
        ) {
          setIsCollapsed(
            preferences.sidebar_collapsed === "1" ||
            preferences.sidebar_collapsed === true
          );
        }
      } catch (error) {
        if (active && error.code !== "ERR_CANCELED") {
          console.error("Error loading preferences:", error);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadUserData();

    return () => {
      active = false;
    };
  }, []);


  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    saveCollapsedState(newCollapsedState);
  };

  const saveCollapsedState = async (collapsed) => {
    try {
      await apiClient.post('/preferences', {
        preferences: {
          sidebar_collapsed: collapsed ? '1' : '0'
        }
      });
    } catch (error) {
      console.error("Error saving collapsed state:", error);
    }
  };

  const toggleSubMenu = (key) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile]);

  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      if (newWidth >= 180 && newWidth <= 400) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      // Save width after resize is complete
      saveWidthToServer(sidebarWidth);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, sidebarWidth]);

  const saveWidthToServer = async (width) => {
    try {
      await apiClient.post('/preferences', {
        preferences: {
          sidebar_width: `${width}px`
        }
      });
    } catch (error) {
      console.error("Error saving sidebar width:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("id");
    localStorage.removeItem("roles");
    localStorage.removeItem("user_type");
    localStorage.removeItem("access_area");
    localStorage.removeItem("user_name");
    navigate("/");
  };

  // Check if user has a specific role
  const hasRole = (roleName) => {
    return userRoles.some(role => role.name === roleName);
  };

  const isPathActive = (path) => {
    if (!path) return false;
    return location.pathname.split('/').filter(Boolean).includes(path);
  };

  // Check if user is admin
  const isAdmin = hasRole('admin');
  
  // Get user name from localStorage
  const userName = localStorage.getItem("user_name") || "User";
  const userInitial = userName.charAt(0).toUpperCase();
  
  // Get display role name from roles
  const getDisplayRole = () => {
    if (isAdmin) return "Administrator";
    if (userRoles.length > 0) {
      // Show the first coordinator role
      const coordinatorRole = userRoles.find(r => r.name !== 'admin');
      if (coordinatorRole) {
        return coordinatorRole.display_name || coordinatorRole.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
    }
    return "User";
  };

  // Helper to check if user has any of the given roles
  const hasAnyRole = (roleNames) => {
    return roleNames.some(roleName => hasRole(roleName));
  };

  // Check for specific coordinator access
  const hasLunchDanaAccess = isAdmin || hasRole('lunch_dana_coordinator');
  const hasBreakfastDanaAccess = isAdmin || hasRole('breakfast_dana_coordinator');
  const hasDanaAccess = hasLunchDanaAccess || hasBreakfastDanaAccess;

  const sidebarItems = [
    {
      key: "events_coordinator",
      label: "Event Management",
      icon: <EventAvailableOutlinedIcon />,
      items: [
        { label: "Create Event", icon: <AddCircleOutlineOutlinedIcon />, path: "event" },
        { label: "Katina Ceremonies", icon: <EventAvailableOutlinedIcon />, path: "katina" },
      ],
    },
    {
      key: "community_service_coordinator",
      label: "Community Services",
      icon: <GroupsOutlinedIcon />,
      items: [
        { label: "Damma Sermons", icon: <RecordVoiceOverOutlinedIcon />, path: "dammaSermens" },
        { label: "Dana At Home", icon: <HomeOutlinedIcon />, path: "danaathome" },
        { label: "Paritta At Home", icon: <HomeOutlinedIcon />, path: "ParittaAtHome" },
      ],
    },
    {
      key: "meditate_with_us_coordinator",
      label: "Meditation",
      icon: <SelfImprovementOutlinedIcon />,
      items: [
        { label: "External Retreats", icon: <ArrowForwardIosOutlinedIcon />, path: "exGlen" },
        { label: "Internal Retreats", icon: <ArrowForwardIosOutlinedIcon />, path: "inReOrg" },
        { label: "Solo Retreats", icon: <ArrowForwardIosOutlinedIcon />, path: "SoloReOnline" },
        { label: "Guest Speakers", icon: <ArrowForwardIosOutlinedIcon />, path: "GuestSpReg" },
        { label: "Ordinations", icon: <ArrowForwardIosOutlinedIcon />, path: "ordinationReq" },
      ],
    },
    {
      key: "dhamma_talks_coordinator",
      label: "Dhamma Talks",
      icon: <RecordVoiceOverOutlinedIcon />,
      items: [
        { label: "Manage Talks", icon: <RecordVoiceOverOutlinedIcon />, path: "SuttaView" },
      ],
    },
    {
      key: "arama_poojawa_coordinator",
      label: "Arama Pooja",
      icon: <TempleBuddhistOutlinedIcon />,
      items: [
        { label: "Five Year Plan", icon: <AssessmentOutlinedIcon />, path: "fiveYear" },
        { label: "Individual Requests", icon: <AddCircleOutlineOutlinedIcon />, path: "inPReq" },
        { label: "Land Lot Requests", icon: <AssessmentOutlinedIcon />, path: "landLot" },
        { label: "Membership", icon: <GroupsOutlinedIcon />, path: "fiveYearMem" },
        { label: "Five Year Requests", icon: <AssessmentOutlinedIcon />, path: "fiveYearReq" },
      ],
    },
    {
      key: "build_up_hermitage_coordinator", 
      label: "Development",
      icon: <ConstructionOutlinedIcon />,
      items: [
        { label: "Future Plans", icon: <AssessmentOutlinedIcon />, path: "featurePlan" },
        { label: "Ongoing Projects", icon: <ConstructionOutlinedIcon />, path: "ongoingReq" },
        { label: "Maintenance", icon: <ConstructionOutlinedIcon />, path: "maintainReq" },
      ],
    },
    {
      key: "donation_coordinator",
      label: "Donations",
      icon: <VolunteerActivismOutlinedIcon />,
      items: [
        { label: "Maintenance Fund", icon: <PaymentsOutlinedIcon />, path: "maintainceFoundRquest" },
        { label: "Sanga Health Fund", icon: <VolunteerActivismOutlinedIcon />, path: "sangaHealthFound" },
        { label: "Life Long Membership", icon: <GroupsOutlinedIcon />, path: "lifeLongMem" },
        { label: "Building Fund", icon: <ConstructionOutlinedIcon />, path: "buildingFund" },
        { label: "Annual Membership", icon: <GroupsOutlinedIcon />, path: "annualMemReq" },
      ],
    },
  ];

  const memberManagementItems = {
    key: "member_management",
    label: "Member Management",
    icon: <PeopleAltOutlinedIcon />,
    items: [
      { label: "Members List", icon: <PeopleAltOutlinedIcon />, path: "memberList" },
      { label: "Member Conflicts", icon: <ContactPhoneOutlinedIcon />, path: "memberConflicts" },
    ],
  };

  // Get visible menu items based on user roles
  const getVisibleMenuItems = () => {
    const visibleItems = [];

    // Add regular menu items
    sidebarItems.forEach(item => {
      const hasAccess = isAdmin || hasRole(item.key);
      if (hasAccess) {
        visibleItems.push({
          ...item,
          type: 'regular',
          id: item.key,
          displayLabel: item.label
        });
      }
    });

    
    // Add Dana menu if accessible
    if (hasDanaAccess) {
      visibleItems.push({
        key: "dana-management",
        id: "dana-management",
        label: "Dana Management",
        displayLabel: "Dana Management",
        icon: <RiceBowlIcon />,
        type: 'dana',
        isDanaParent: true
      });
    }
    
    return visibleItems;
  };

  // Apply saved order to visible items
  const getOrderedMenuItems = () => {
    const visibleItems = getVisibleMenuItems();
    
    if (menuOrder.length === 0) {
      return visibleItems;
    }
    
    // Sort visible items based on saved order
    const orderedItems = [];
    menuOrder.forEach(id => {
      const item = visibleItems.find(item => item.id === id);
      if (item) {
        orderedItems.push(item);
      }
    });
    
    // Add any new items that aren't in the saved order
    visibleItems.forEach(item => {
      if (!orderedItems.find(oi => oi.id === item.id)) {
        orderedItems.push(item);
      }
    });
    
    return orderedItems;
  };

  // --- Updated Dana menu items with "Annual Requests" first ---
  const getDanaMenuItems = () => {
    const items = [];
    
    // Breakfast Annual Requests
    if (isAdmin || hasRole('breakfast_dana_coordinator')) {
      items.push(
        { label: "Breakfast Annual Requests", icon: <EventRepeatOutlinedIcon />, path: "breakfast-dana-annual-requests" }
      );
    }
    
    // Breakfast Requests
    if (isAdmin || hasRole('breakfast_dana_coordinator')) {
      items.push(
        { label: "Breakfast Requests", icon: <FreeBreakfastOutlinedIcon />, path: "breakfast-dana-requests" }
      );
    }
    
    // Breakfast Payment Requests
    if (isAdmin || hasRole('breakfast_dana_coordinator')) {
      items.push(
        { label: "Breakfast Payment Requests", icon: <PaymentsOutlinedIcon />, path: "breakfast-dana-payment-requests" }
      );
    }
    
    // Breakfast Availability
    if (isAdmin || hasRole('breakfast_dana_coordinator')) {
      items.push(
        { label: "Breakfast Availability", icon: <EventAvailableOutlinedIcon />, path: "breakfast-availability" }
      );
    }

    // Lunch Annual Requests
    if (isAdmin || hasRole('lunch_dana_coordinator')) {
      items.push(
        { label: "Lunch Annual Requests", icon: <EventRepeatOutlinedIcon />, path: "Lunch-dana-annual-requests" }
      );
    }
    
    // Lunch Requests
    if (isAdmin || hasRole('lunch_dana_coordinator')) {
      items.push(
        { label: "Lunch Requests", icon: <LunchDiningOutlinedIcon />, path: "lunch-dana-requests" }
      );
    }
    
    // Lunch Payment Requests
    if (isAdmin || hasRole('lunch_dana_coordinator')) {
      items.push(
        { label: "Lunch Payment Requests", icon: <PaymentsOutlinedIcon />, path: "lunch-dana-payment-requests" }
      );
    }
    
    // Lunch Availability
    if (isAdmin || hasRole('lunch_dana_coordinator')) {
      items.push(
        { label: "Lunch Availability", icon: <EventAvailableOutlinedIcon />, path: "lunch-availability" }
      );
    }

    // Gilanpasa and Pirikara
    if (isAdmin || hasRole('lunch_dana_coordinator')) {
      items.push(
        { label: "Gilanpasa Requests", icon: <LocalCafeOutlinedIcon />, path: "dana-gilanpasa" },
        { label: "Gilanpasa Availability", icon: <LocalCafeOutlinedIcon />, path: "dana-gilanpasa-availability" },
        { label: "Pirikara Requests", icon: <VolunteerActivismOutlinedIcon />, path: "dana-pirikara-requests" },
        { label: "Pirikara Items", icon: <Inventory2OutlinedIcon />, path: "dana-pirikara-items" }
      );
    }
    
    return items;
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const newOrder = [...tempMenuOrder];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      setTempMenuOrder(newOrder);
    }
  };

  const handleMoveDown = (index) => {
    if (index < tempMenuOrder.length - 1) {
      const newOrder = [...tempMenuOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setTempMenuOrder(newOrder);
    }
  };

  const openReorderDialog = () => {
    // Get current visible items
    const visibleItems = getVisibleMenuItems();
    
    // Use existing menu order if available, otherwise use default order
    let currentOrder = [];
    if (menuOrder.length > 0) {
      // Filter menuOrder to only include IDs that are still visible
      currentOrder = menuOrder.filter(id => visibleItems.some(item => item.id === id));
      
      // Add any new visible items that aren't in the saved order
      visibleItems.forEach(item => {
        if (!currentOrder.includes(item.id)) {
          currentOrder.push(item.id);
        }
      });
    } else {
      currentOrder = visibleItems.map(item => item.id);
    }
    setTempMenuOrder(currentOrder);
    setReorderDialogOpen(true);
  };

  const saveReorder = async () => {
    try {
      const response = await apiClient.post('/preferences', {
        preferences: {
          sidebar_menu_order: tempMenuOrder
        }
      });

      // If we reach here, request succeeded (Axios throws on non‑2xx)
      setMenuOrder(tempMenuOrder);
      setReorderDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Menu order saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error saving menu order:", error);
      let errorMessage = 'Network error. Please try again.';
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || 'Failed to save menu order';
      } else if (error.message) {
        errorMessage = error.message;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const openWidthDialog = () => {
    setTempWidth(sidebarWidth);
    setWidthDialogOpen(true);
  };

  const saveWidth = async () => {
    try {
      await apiClient.post('/preferences', {
        preferences: { sidebar_width: `${tempWidth}px` }
      });
      setSidebarWidth(tempWidth);
      setWidthDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Sidebar width saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error("Error saving sidebar width:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Network error. Please try again.',
        severity: 'error'
      });
    }
  };

  const orderedMenuItems = getOrderedMenuItems();

  // Calculate the margin left for main content
  const getMainContentMargin = () => {
    if (isCollapsed) return "80px";
    return `${sidebarWidth}px`;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100vh", position: "relative" }}>
      <Sidebar
        collapsed={isCollapsed}
        backgroundColor="#ffffff"
        rootStyles={{
          border: "none",
          boxShadow: "2px 0 8px rgba(0, 0, 0, 0.05)",
        }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 1000,
          width: isCollapsed ? "80px" : `${sidebarWidth}px`,
          transition: isResizing ? 'none' : 'width 0.2s ease',
        }}
        ref={sidebarRef}
      >
        <Menu
          menuItemStyles={{
            button: {
              backgroundColor: "transparent",
              color: "#4b5563",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "#f3f4f6",
                color: "#1976d2",
              },
            },
          }}
        >
          {/* Header with Logo and Toggle */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              mb: 2,
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            {!isCollapsed && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: "#1976d2", 
                    width: 32, 
                    height: 32,
                    fontSize: "1rem",
                    fontWeight: 600 
                  }}
                >
                  RB
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#111827",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    fontSize: sidebarWidth < 220 ? '0.9rem' : '1.25rem',
                  }}
                >
                  Ripplebrook
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isCollapsed && !isMobile && (
                <Tooltip title="Adjust Width">
                  <IconButton 
                    size="small"
                    onClick={openWidthDialog}
                    sx={{ 
                      color: "#6b7280",
                      "&:hover": { color: "#1976d2" }
                    }}
                  >
                    <WidthNormalIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton 
                onClick={toggleSidebar}
                sx={{ 
                  color: "#6b7280",
                  "&:hover": { 
                    color: "#1976d2",
                    backgroundColor: "#f3f4f6"
                  }
                }}
              >
                <MenuOutlinedIcon />
              </IconButton>
            </Box>
          </Box>

          {/* User Profile Section */}
          {!isCollapsed && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                mb: 2,
                backgroundColor: "#f9fafb",
                borderRadius: "12px",
                mx: 2,
                border: "1px solid #e5e7eb",
              }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: "#1976d2",
                  width: 40,
                  height: 40,
                }}
              >
                {userInitial}
              </Avatar>
              <Box>
                <Typography sx={{ color: "#111827", fontWeight: 600, fontSize: "0.9rem" }}>
                  {userName}
                </Typography>
                <Typography sx={{ color: "#6b7280", fontSize: "0.75rem" }}>
                  {getDisplayRole()}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Dashboard Link */}
          <Item
            title="Dashboard"
            to="dashboard"
            icon={<DashboardOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
            collapsed={isCollapsed}
          />

          <Divider sx={{ my: 2, borderColor: "#e5e7eb" }} />

          {/* User Management Section - Admin only */}
          {isAdmin && (
            <>
              {!isCollapsed && (
                <Typography
                  sx={{
                    px: 3,
                    py: 1,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: "#9ca3af",
                  }}
                >
                  User Management
                </Typography>
              )}

              <SubMenu
                label="User Management"
                icon={<AdminPanelSettingsOutlinedIcon />}
                open={openSubMenus["userManagement"]}
                onClick={() => toggleSubMenu("userManagement")}
                style={{
                  backgroundColor: location.pathname.includes("memeberView") || location.pathname.includes("userList") ? "#e3f2fd" : "transparent",
                  color: location.pathname.includes("memeberView") || location.pathname.includes("userList") ? "#1976d2" : "inherit",
                  fontWeight: location.pathname.includes("memeberView") || location.pathname.includes("userList") ? 600 : 400,
                }}
              >
                <MenuItem 
                  icon={<PersonAddAltOutlinedIcon />}
                  style={{ 
                    marginLeft: "10px",
                    backgroundColor: location.pathname.includes("memeberView") ? "#e3f2fd" : "transparent",
                    color: location.pathname.includes("memeberView") ? "#1976d2" : "inherit",
                  }}
                  onClick={() => navigate("/admin/memeberView")}
                >
                  <Typography>Register User</Typography>
                </MenuItem>
                <MenuItem 
                  icon={<PeopleAltOutlinedIcon />}
                  style={{ 
                    marginLeft: "10px",
                    backgroundColor: location.pathname.includes("userList") ? "#e3f2fd" : "transparent",
                    color: location.pathname.includes("userList") ? "#1976d2" : "inherit",
                  }}
                  onClick={() => navigate("/admin/userList")}
                >
                  <Typography>User List</Typography>
                </MenuItem>
              </SubMenu>

              <Divider sx={{ my: 2, borderColor: "#e5e7eb" }} />
            </>
          )}

                    {/* Member Management Section - visible to Admin and Dana Coordinators */}
          {!isCollapsed && (isAdmin || hasRole('breakfast_dana_coordinator') || hasRole('lunch_dana_coordinator')) && (
            <Typography
              sx={{
                px: 3,
                py: 1,
                fontSize: "0.7rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#9ca3af",
              }}
            >
              Member Management
            </Typography>
          )}

          { (isAdmin || hasRole('breakfast_dana_coordinator') || hasRole('lunch_dana_coordinator')) && (
            <SubMenu
              label="Member Management"
              icon={<PeopleAltOutlinedIcon />}
              open={openSubMenus["memberManagement"]}
              onClick={() => toggleSubMenu("memberManagement")}
              style={{
                backgroundColor: location.pathname.includes("memberList") || location.pathname.includes("memberConflicts") ? "#e3f2fd" : "transparent",
                color: location.pathname.includes("memberList") || location.pathname.includes("memberConflicts") ? "#1976d2" : "inherit",
                fontWeight: location.pathname.includes("memberList") || location.pathname.includes("memberConflicts") ? 600 : 400,
              }}
            >
              <MenuItem 
                icon={<PeopleAltOutlinedIcon />}
                style={{ 
                  marginLeft: "10px",
                  backgroundColor: location.pathname.includes("memberList") ? "#e3f2fd" : "transparent",
                  color: location.pathname.includes("memberList") ? "#1976d2" : "inherit",
                }}
                onClick={() => navigate("/admin/memberList")}
              >
                <Typography>Members List</Typography>
              </MenuItem>
              <MenuItem 
                icon={<ContactPhoneOutlinedIcon />}
                style={{ 
                  marginLeft: "10px",
                  backgroundColor: location.pathname.includes("memberConflicts") ? "#e3f2fd" : "transparent",
                  color: location.pathname.includes("memberConflicts") ? "#1976d2" : "inherit",
                }}
                onClick={() => navigate("/admin/memberConflicts")}
              >
                <Typography>Member Conflicts</Typography>
              </MenuItem>
            </SubMenu>
          )}

          <Divider sx={{ my: 2, borderColor: "#e5e7eb" }} />

          {/* Management Areas Section */}
          {!isCollapsed && orderedMenuItems.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, py: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#9ca3af",
                }}
              >
                Management Areas
              </Typography>
              {!isCollapsed && (
                <Tooltip title="Reorder Menu">
                  <IconButton 
                    size="small" 
                    onClick={openReorderDialog}
                    sx={{ 
                      color: "#9ca3af",
                      "&:hover": { color: "#1976d2" }
                    }}
                  >
                    <ReorderIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {/* Menu Items - Render in saved order */}
          {orderedMenuItems.map((item) => {
            if (item.type === 'dana') {
              const danaItems = getDanaMenuItems();
              if (danaItems.length === 0) return null;
              
              const isAnyDanaChildActive = danaItems.some(subItem => 
                isPathActive(subItem.path)
              );
              
              return (
                <Box key={item.id}>
                  <SubMenu
                    label={item.label}
                    icon={item.icon}
                    open={openSubMenus[item.id]}
                    onClick={() => toggleSubMenu(item.id)}
                    style={{
                      backgroundColor: isAnyDanaChildActive ? "#e3f2fd" : "transparent",
                      color: isAnyDanaChildActive ? "#1976d2" : "inherit",
                      fontWeight: isAnyDanaChildActive ? 600 : 400,
                    }}
                  >
                    {danaItems.map((subItem) => {
                      const isActive = isPathActive(subItem.path);
                      return (
                        <MenuItem 
                          key={subItem.path}
                          icon={subItem.icon}
                          style={{ 
                            marginLeft: "10px",
                            backgroundColor: isActive ? "#e3f2fd" : "transparent",
                            color: isActive ? "#1976d2" : "inherit",
                          }}
                          onClick={() =>  navigate(`/admin/${subItem.path.replace(/^\/+/, "")}`)}
                        >
                          <Typography sx={{ fontWeight: isActive ? 600 : 400 }}>
                            {subItem.label}
                          </Typography>
                        </MenuItem>
                      );
                    })}
                  </SubMenu>
                </Box>
              );
            } else {
              if (!item.items || item.items.length === 0) return null;
              
              const isAnyChildActive = item.items.some(subItem => 
                isPathActive(subItem.path)
              );
              
              return (
                <Box key={item.id}>
                  <SubMenu
                    label={item.label}
                    icon={item.icon}
                    open={openSubMenus[item.id]}
                    onClick={() => toggleSubMenu(item.id)}
                    style={{
                      backgroundColor: isAnyChildActive ? "#e3f2fd" : "transparent",
                      color: isAnyChildActive ? "#1976d2" : "inherit",
                      fontWeight: isAnyChildActive ? 600 : 400,
                    }}
                  >
                    {item.items.map((subItem) => {
                      const isActive = isPathActive(subItem.path);
                      return (
                        <MenuItem 
                          key={subItem.path}
                          icon={subItem.icon}
                          style={{ 
                            marginLeft: "10px",
                            backgroundColor: isActive ? "#e3f2fd" : "transparent",
                            color: isActive ? "#1976d2" : "inherit",
                          }}
                          onClick={() =>  navigate(`/admin/${subItem.path.replace(/^\/+/, "")}`)}
                        >
                          <Typography sx={{ fontWeight: isActive ? 600 : 400 }}>
                            {subItem.label}
                          </Typography>
                        </MenuItem>
                      );
                    })}
                  </SubMenu>
                </Box>
              );
            }
          })}

          <Divider sx={{ my: 2, borderColor: "#e5e7eb" }} />

          {/* Audit Section */}
          {!isCollapsed && (
            <Typography
              sx={{
                px: 3,
                py: 1,
                fontSize: "0.7rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#9ca3af",
              }}
            >
              Audit
            </Typography>
          )}

          <Item
            title="General Enquiries"
            to="general"
            icon={<HelpOutlineOutlinedIcon />}
            selected={selected}
            setSelected={setSelected}
            collapsed={isCollapsed}
          />

          {isAdmin && (
            <Item
              title="Admin Audit"
              to="adminAudit"
              icon={<HistoryOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              collapsed={isCollapsed}
            />
          )}

          {isAdmin && (
            <Item
              title="User Audit"
              to="userAudit"
              icon={<AssessmentOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              collapsed={isCollapsed}
            />
          )}

          <Divider sx={{ my: 2, borderColor: "#e5e7eb" }} />

          {/* System Section */}
          {!isCollapsed && (
            <Typography
              sx={{
                px: 3,
                py: 1,
                fontSize: "0.7rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#9ca3af",
              }}
            >
              System
            </Typography>
          )}

          {isAdmin && (
            <Item
              title="Contact Settings"
              to="contact-settings"
              icon={<SettingsOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
              collapsed={isCollapsed}
            />
          )}

          <MenuItem
            icon={<LogoutOutlinedIcon />}
            onClick={handleLogout}
            style={{
              color: "#dc2626",
            }}
          >
            Logout
          </MenuItem>
        </Menu>
      </Sidebar>

      {/* Resize Handle */}
      {!isCollapsed && !isMobile && (
        <Box
          sx={{
            position: "fixed",
            left: `${sidebarWidth}px`,
            top: 0,
            bottom: 0,
            width: "4px",
            cursor: "ew-resize",
            backgroundColor: isResizing ? "#1976d2" : "transparent",
            transition: "background-color 0.2s",
            zIndex: 1001,
            "&:hover": {
              backgroundColor: "#1976d2",
              opacity: 0.3,
            },
          }}
          onMouseDown={() => setIsResizing(true)}
        />
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginLeft: getMainContentMargin(),
          transition: isResizing ? 'none' : 'margin-left 0.2s ease',
          backgroundColor: "#f9fafb",
          minHeight: "100vh",
          width: `calc(100% - ${getMainContentMargin()})`,
          overflowX: "auto",
        }}
      >
        <Outlet />
      </Box>

      {/* Reorder Dialog */}
      <Dialog 
        open={reorderDialogOpen} 
        onClose={() => setReorderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReorderIcon />
            <Typography variant="h6">Reorder Menu Items</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use arrows to change the order of menu items
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <List>
              {tempMenuOrder.map((id, index) => {
                const allVisibleItems = getVisibleMenuItems();
                const item = allVisibleItems.find(i => i.id === id);
                if (!item) return null;
                return (
                  <ListItem
                    key={id}
                    sx={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: '#f9fafb'
                    }}
                    secondaryAction={
                      <Box>
                        <IconButton 
                          edge="end" 
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          size="small"
                        >
                          <ArrowUpwardIcon />
                        </IconButton>
                        <IconButton 
                          edge="end" 
                          onClick={() => handleMoveDown(index)}
                          disabled={index === tempMenuOrder.length - 1}
                          size="small"
                        >
                          <ArrowDownwardIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemIcon>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.displayLabel} />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReorderDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveReorder} variant="contained" color="primary">
            Save Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Width Adjustment Dialog */}
      <Dialog 
        open={widthDialogOpen} 
        onClose={() => setWidthDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WidthNormalIcon />
            <Typography variant="h6">Adjust Sidebar Width</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Drag the slider to adjust the sidebar width (180px - 400px)
          </Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={tempWidth}
              onChange={(e, newValue) => setTempWidth(newValue)}
              min={180}
              max={400}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}px`}
              marks={[
                { value: 180, label: '180px' },
                { value: 250, label: '250px' },
                { value: 320, label: '320px' },
                { value: 400, label: '400px' },
              ]}
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body1" fontWeight="bold">
              Current width: {tempWidth}px
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWidthDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveWidth} variant="contained" color="primary">
            Save Width
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SidebarComponent;